import { parse as parseYaml } from 'yaml'

export type StatusBoardHotPickup = {
  item: string
  next: string
  noteDate: string
  bet?: string
}

export type StatusBoardBlocked = {
  item: string
  blockedOn: string
  owner: string
}

export type StatusBoardFence = {
  lastBoardUpdate: string
  hotPickups: StatusBoardHotPickup[]
  blocked: StatusBoardBlocked[]
}

const FENCE_RE = /```yaml(?:\s+status-board)?\s*\r?\n([\s\S]*?)\r?\n```/i

function scalarDate(value: unknown): string {
  if (value == null) return ''
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10)
  }
  return String(value).trim()
}

/** True when a TipTap / markdown code fence is a STATUS-BOARD attention queue. */
export function isStatusBoardFenceLanguage(language: string, text: string): boolean {
  const lang = (language || '').toLowerCase().trim()
  if (lang === 'status-board' || lang.includes('status-board')) return true
  if (lang === 'yaml' || lang.startsWith('yaml ')) {
    return /hot_pickups\s*:/i.test(text) && /last_board_update\s*:/i.test(text)
  }
  return /hot_pickups\s*:/i.test(text) && /last_board_update\s*:/i.test(text)
}

/** Parse ```yaml status-board fence body (or full document containing one). */
export function parseStatusBoardFence(raw: string): StatusBoardFence | null {
  if (!raw?.trim()) return null
  let yamlBody = raw.trim()
  const m = raw.match(FENCE_RE)
  if (m) yamlBody = m[1]
  else if (!/hot_pickups\s*:/i.test(raw) || !/last_board_update\s*:/i.test(raw)) {
    return null
  }

  try {
    const data = parseYaml(yamlBody) as Record<string, unknown> | null
    if (!data || typeof data !== 'object') return null

    const hotPickups: StatusBoardHotPickup[] = []
    const rows = Array.isArray(data.hot_pickups) ? data.hot_pickups : []
    for (const row of rows) {
      if (!row || typeof row !== 'object') continue
      const r = row as Record<string, unknown>
      const item = r.item != null ? String(r.item).trim() : ''
      if (!item) continue
      hotPickups.push({
        item,
        next: r.next != null ? String(r.next).trim() : '',
        noteDate: scalarDate(r.note_date),
        bet: r.bet != null ? String(r.bet).trim() : undefined,
      })
    }

    const blocked: StatusBoardBlocked[] = []
    const blockedRows = Array.isArray(data.blocked) ? data.blocked : []
    for (const row of blockedRows) {
      if (!row || typeof row !== 'object') continue
      const r = row as Record<string, unknown>
      const item = r.item != null ? String(r.item).trim() : ''
      if (!item) continue
      blocked.push({
        item,
        blockedOn: r.blocked_on != null ? String(r.blocked_on).trim() : '',
        owner: r.owner != null ? String(r.owner).trim() : '',
      })
    }

    return {
      lastBoardUpdate: scalarDate(data.last_board_update),
      hotPickups,
      blocked,
    }
  } catch {
    return null
  }
}

/** Strip the first status-board fence from markdown body (for optional body cleanup). */
export function stripStatusBoardFence(markdown: string): string {
  return markdown.replace(FENCE_RE, '').replace(/\n{3,}/g, '\n\n').trimStart()
}
